import { Heading, Button } from '@simplywallst/ui-core/next';
import { useMemo, useState } from 'react';
import type { PortfolioDetailedReturns } from '../../../../hooks/usePortfolioDetailedReturns';
import { cn } from '@simplywallst/ui-core/next';

import Tooltip from '@/components/Tooltip';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { ContributorChart } from './ContributorChart';
import { TransactionPortfolioUpgrade } from '../../../TransactionPortfolioUpgrade';
import { usePortfolioType } from '../../../../../HoldingsTable/hooks';

type Props = {
  portfolioDetailedReturns: PortfolioDetailedReturns;
};

const getTopContributors = (holdings: PortfolioDetailedReturns['holdings']) => {
  // Filter out holdings with 0% gains and sort by total gains
  const nonZeroHoldings = holdings
    .filter((holding) => holding.detailedReturns.totalGains !== 0)
    .sort(
      (a, b) => b.detailedReturns.totalGains - a.detailedReturns.totalGains
    );

  if (nonZeroHoldings.length === 0) {
    return {
      topGainers: [],
      topLosers: [],
    };
  }

  if (nonZeroHoldings.length === 1) {
    const holding = nonZeroHoldings[0];
    return {
      topGainers: holding.detailedReturns.totalGains > 0 ? [holding] : [],
      topLosers: holding.detailedReturns.totalGains < 0 ? [holding] : [],
    };
  }

  // Show all holdings if 10 or fewer, split evenly with extra going to top
  if (nonZeroHoldings.length <= 10) {
    const halfLength = Math.ceil(nonZeroHoldings.length / 2);
    return {
      topGainers: nonZeroHoldings.slice(0, halfLength),
      topLosers: nonZeroHoldings.slice(halfLength),
    };
  }

  // For more than 10 holdings, limit to 5 per column
  return {
    topGainers: nonZeroHoldings.slice(0, 5),
    topLosers: nonZeroHoldings.slice(-5).reverse(),
  };
};

export const TopContributorsChart = ({ portfolioDetailedReturns }: Props) => {
  const { holdings, currencyISO, liquidatedHoldings } =
    portfolioDetailedReturns;

  const portfolioType = usePortfolioType(portfolioDetailedReturns);

  const isHoldingsPortfolio = portfolioType !== 'transaction-portfolio';

  const [showAll, setShowAll] = useState(false);

  const { topGainers, topLosers } = useMemo(
    () => getTopContributors([...holdings, ...liquidatedHoldings]),
    [holdings, liquidatedHoldings]
  );

  const highestGain = useMemo(() => {
    return Math.max(
      ...[...holdings, ...liquidatedHoldings].map((h) =>
        Math.abs(h.detailedReturns.totalGains)
      )
    );
  }, [holdings, liquidatedHoldings]);

  const shouldShowMoreButton = useMemo(() => {
    return [...holdings, ...liquidatedHoldings].length > 10;
  }, [holdings, liquidatedHoldings]);

  return (
    <div className="grid grid-rows-[min-content_min-content] gap-x3 bg-surface-1 px-x2 py-x3 md:rounded-x1 md:px-x4">
      <div className="grid grid-cols-[max-content_max-content] items-center justify-between gap-x-1">
        <Heading
          as="h2"
          size="large"
          weight="strong"
          className="grid grid-cols-[max-content_24px] items-center gap-x-1 text-solid"
        >
          <span>Contributors to Returns</span>
          <Tooltip
            title={
              <div className="max-w-[260px]">
                <p className="m-0 text-left text-tiny text-legacy-spacegray">
                  This chart shows the highest and lowest contributors to your
                  return over the selected period.
                </p>
              </div>
            }
            light
            placement="top"
            zIndex={99999}
          >
            <InformationSolid className="fill-subtle" />
          </Tooltip>
        </Heading>
        {isHoldingsPortfolio && <TransactionPortfolioUpgrade />}
      </div>
      <div className="grid items-start gap-x3 md:grid-cols-2 md:gap-x4">
        <div className="grid gap-x2" data-testid="highest-contributors">
          <Heading as="h3" size="xSmall" className="text-soft">
            Highest contributors
          </Heading>
          <div className="relative grid gap-x2">
            {topGainers.length === 0 && topLosers.length === 0 ? (
              <div className="text-tiny text-softer">
                You have no holdings with returns in this portfolio
              </div>
            ) : topGainers.length === 0 && topLosers.length === 1 ? (
              <div className="text-tiny text-softer">
                You have 1 holding with returns in this portfolio
              </div>
            ) : (
              topGainers.map((holding, index) => (
                <div
                  key={holding.id}
                  data-testid={`contributor-${holding.id}`}
                  className={cn(!showAll && index >= 3 && 'hidden md:block')}
                >
                  <ContributorChart
                    holding={holding}
                    highestGain={highestGain}
                    currencyISO={currencyISO}
                  />
                </div>
              ))
            )}
            {!showAll && topGainers.length === 5 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-1 h-24 [background:linear-gradient(180deg,rgba(27,34,45,0)_0%,#1B222D_100%)] md:hidden" />
            )}
          </div>
        </div>
        <div className="grid gap-x2" data-testid="lowest-contributors">
          <Heading as="h3" size="xSmall" className="text-soft">
            Lowest contributors
          </Heading>
          <div className="relative grid gap-x2">
            {topLosers.length === 0 && topGainers.length === 0 ? (
              <div className="text-tiny text-softer">
                You have no holdings with returns in this portfolio
              </div>
            ) : topLosers.length === 0 && topGainers.length === 1 ? (
              <div className="text-tiny text-softer">
                You have 1 holding with returns in this portfolio
              </div>
            ) : (
              topLosers.map((holding, index) => (
                <div
                  key={holding.id}
                  data-testid={`contributor-${holding.id}`}
                  className={cn(!showAll && index >= 3 && 'hidden md:block')}
                >
                  <ContributorChart
                    holding={holding}
                    highestGain={highestGain}
                    currencyISO={currencyISO}
                  />
                </div>
              ))
            )}
            {!showAll && topLosers.length === 5 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-1 h-24 [background:linear-gradient(180deg,rgba(27,34,45,0)_0%,#1B222D_100%)] md:hidden" />
            )}
          </div>
        </div>
        {shouldShowMoreButton && (
          <div className="w-full md:hidden">
            <Button
              variant="secondary"
              size="medium"
              className="w-full"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'See less' : 'See top 5 highest and lowest'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
